
import React, { useRef, useState, useEffect } from 'react';
import { db } from 'config/firebase';
import { addDoc, collection, query, where, getDoc, getDocs, deleteDoc, doc, collectionGroup } from 'firebase/firestore';
import { Modal, Button, Tabs, Tab, Form, ListGroup, Row, Col, InputGroup, Image } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import DatePicker from "react-datepicker";
import { debounce } from 'lodash';
import { calculateFromMargin, formatCurrency } from '../utils/pricingCalculations';
import { PriceBreakdownCard } from '../components/PriceBreakdownCard';

export const ManagePricingTemplatesModal = ({ show, onClose }) => {

    const [templates, setTemplates] = useState([]);
    useEffect(() => {
        let isSubbed = true;
        const getTemplates = async () => {
            const q = query(
                collection(db, 'systemTemplates'),
                where('type', '==', 'pricing')
            );
            const querySnapshot = await getDocs(q);
            const templateData = [];
            querySnapshot.forEach((doc) => {
                templateData.push({ id: doc.id, ref: doc.ref, ...doc.data() });
            });
            return templateData;
        }
        getTemplates().then((data) => {
            if (isSubbed) {
                setTemplates(data);
            }
        });
        return () => isSubbed = false;
    }, []);

    return (
        <Modal show={show} onHide={onClose} size="fullscreen">
            <Modal.Header>
                <Modal.Title>Manage Pricing Templates</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Tabs defaultActiveKey="createNew" id="pricingTemplateTabs" className='m-1'>
                    <Tab eventKey="createNew" title="Create New Template" className='border-bottom border-x p-3'>
                        <NewPricingTemplateForm />
                    </Tab>
                    <Tab eventKey="manage" title="Manage Existing" className='border-bottom border-x p-3'>
                        <ManageExistingTemplatesForm templates={templates}/>
                    </Tab>
                </Tabs>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
    
}

const NewPricingTemplateForm = () => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [simSku, setSimSku] = useState(null);
    const simSkuInput = useRef();
    const [simSkuSearchResults, setSearchResults] = useState([]);
    const [simSkuData, setSimSkuData] = useState(null);
    const [errorState, setErrorState] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [simulationRunning, setSimulationRunning] = useState(false);
    const [productImageUrl, setProductImageUrl] = useState(null);

    const additionalFulfillmentCostsRef = useRef();
    const [afc, setAfc] = useState(0);
    const additionalChannelCostsRef = useRef();
    const [acc, setAcc] = useState(0);
    const additionalInboundCostsRef = useRef();
    const [aic, setAic] = useState(0);
    const templateNameRef = useRef();

    const minMarginRef = useRef();
    const [minMarginValue, setMinMarginValue] = useState(0);
    const targetMarginRef = useRef();
    const [targetMarginValue, setTargetMarginValue] = useState(0);
    const b2bMarginRef = useRef();
    const [b2bMarginValue, setB2bMarginValue] = useState(0);
    const maxMarginRef = useRef();
    const [maxMarginValue, setMaxMarginValue] = useState(0);

    const debounceFunction = (func, delay) => {
        return debounce(func, delay);
    };

    const handleNewTemplateFormSubmission = async (e) => {
        e.preventDefault();
        // Convert percentage input to decimal and store in variables

        const newName = templateNameRef.current.value;
        if (!newName.trim()) {
            toast.error('Template name is required', { theme: 'colored' });
            return;
        }

        const newTemplate = {
            name: newName,
            settings: {
                minMargin: minMarginValue,
                maxMargin: maxMarginValue,
                prefMargin: targetMarginValue,
                bizMargin: b2bMarginValue,
                additionalFulfillmentCost: afc,
                additionalChannelCost: acc,
                additionalInboundCost: aic,
            },
            type: 'pricing',
            timestamp: {
                created: new Date(),
                updated: new Date(),
            }
        }


        try {
            await addDoc(collection(db, 'systemTemplates'), newTemplate)
            toast.success('New template created', { theme: 'colored' });
            // wait a half sec then close modal
            setTimeout(() => {
                onClose();
            }, 500);
        } catch (error) {
            console.log(error);
            toast.error(error.message, { theme: 'colored' });
        }

    }

    const lookupSkuForSimulation = async (e) => {
        e.preventDefault();
        const lookupSku = simSkuInput.current.value;
        if (!lookupSku.trim()) {
            toast.error('SKU is required', { theme: 'colored' });
            return;
        } else {
            const results = await getProductData(lookupSku);
            setSearchResults(results);
            return;
        }
    }

    const getProductData = async (sku) => {
        // build a less than variable that starts with the sku and appends high value ascii characters
        // this will allow us to query for all skus that start with the sku
        // then we can filter the results to find the exact match
        const skuEnd = sku + '~~~~~~~~~~~';
        try {
            const q = query(
                collectionGroup(db, 'variants'),
                where('sku', '>=', sku),
                where('sku', '<=', skuEnd),
            );
            const querySnapshot = await getDocs(q);
            const productData = [];
            querySnapshot.forEach((doc) => {
                productData.push({ id: doc.id, ref: doc.ref, ...doc.data() });
            });
            return productData;
        } catch (error) {
            console.log(error)
        }
    }

    const runSimulation = async (skuData) => {
        setSimulationRunning(true);
        const parentRef = skuData.parentRef;
        if (parentRef) {
            const parentDoc = await getDoc(parentRef);
            const parentData = parentDoc.data();
            const cost = parentData.cost;
            if (cost && skuData.productFactor) {
                skuData.cost = cost * skuData.productFactor;
            }
            if (parentData.primaryImage) {
                setProductImageUrl(parentData.primaryImage);
            }
        }
        if (skuData.weightOz) {
            const shippingCost = await lookupShippingCost(skuData.weightOz);
            if (shippingCost) {
                skuData.quickShippingCost = shippingCost
            } else {
                skuData.quickShippingCost = 0.00;
            }
            // add additional costs to base cost
            let baseCost = skuData.cost;
            // if there are additional costs, add them to the base cost
            if (afc) { // fullfillment cost
                baseCost += Number(afc);
            }
            if (acc) { // channel cost
                baseCost += Number(acc);
            }
            if (aic) { // inbound cost
                baseCost += Number(aic);
            }
            // Calculate marketplace fees based on estimated price
            let marketplaceFees = 0.15;
            const interim = Number(baseCost) + Number(skuData.quickShippingCost) + Number(baseCost * marketplaceFees);
            if (interim < 10) {
                marketplaceFees = 0.08;
            }

            // Calculate total cost basis (COGS + additional costs + shipping)
            const totalCostBasis = Number(baseCost) + Number(shippingCost);

            // Use explicit margin formula: Price = Cost / (1 - Margin%)
            // Margin values are stored as decimals (e.g., 0.30 for 30%), multiply by 100 for percentage
            const calculatePriceWithMarginAndFees = (cost, marginDecimal, feeRate) => {
                try {
                    const marginPercent = marginDecimal * 100;
                    const priceData = calculateFromMargin(cost, marginPercent);
                    // Add marketplace fees to the calculated price
                    const priceWithFees = priceData.price + (priceData.price * feeRate);
                    return {
                        ...priceData,
                        priceWithFees: Math.round(priceWithFees * 100) / 100,
                    };
                } catch (error) {
                    // Handle margin >= 100% error gracefully
                    return { price: 0, marginAmount: 0, marginPercent: marginDecimal * 100, markupPercent: 0, priceWithFees: 0 };
                }
            };

            // Calculate prices at each margin tier using the explicit margin formula
            const minPriceData = calculatePriceWithMarginAndFees(totalCostBasis, minMarginValue, marketplaceFees);
            const targetPriceData = calculatePriceWithMarginAndFees(totalCostBasis, targetMarginValue, marketplaceFees);
            const maxPriceData = calculatePriceWithMarginAndFees(totalCostBasis, maxMarginValue, marketplaceFees);
            const bizPriceData = calculatePriceWithMarginAndFees(totalCostBasis, b2bMarginValue, marketplaceFees);

            // Store breakdown data for PriceBreakdownCard
            skuData.priceBreakdown = {
                cogs: Number(skuData.cost) || 0,
                fulfillmentCost: Number(skuData.quickShippingCost) || 0,
                fulfillmentSource: 'weightEstimate',
                additionalFees: (Number(afc) || 0) + (Number(acc) || 0) + (Number(aic) || 0),
                totalCost: totalCostBasis,
                price: targetPriceData.priceWithFees,
                marginAmount: targetPriceData.marginAmount,
                marginPercent: targetPriceData.marginPercent,
                markupPercent: targetPriceData.markupPercent,
            };

            // Store prices for display
            skuData.minPrice = minPriceData.priceWithFees;
            skuData.maxPrice = maxPriceData.priceWithFees;
            skuData.targetPrice = targetPriceData.priceWithFees;
            skuData.businessPrice = bizPriceData.priceWithFees;
            skuData.marketplaceFees = marketplaceFees;
            skuData.baseCost = baseCost;
            skuData.totalCostBasis = totalCostBasis;
        }
        setSimulationRunning(false);
        return skuData;
    }

    const clearSimulation = (e) => {
        e.preventDefault();
        setSimSku(null);
        setSimSkuData(null);
        setSearchResults([]);
        setErrorState(false);
        setErrorMessage(null);
    }

    const selectSkuForSimulation = async (sku) => {
        setSimSku(sku);
        // find the sku in the search results and set the data
        const skuData = simSkuSearchResults.find((result) => result.id === sku);
        const simulatedSkuData = await runSimulation(skuData);
        setSimSkuData(simulatedSkuData);
    }

    const lookupShippingCost = async (weightOz) => {
        if (!weightOz) throw new Error('Weight is required');
        const weightRange = weightOz * 0.1;
        const floorWeight = weightOz - weightRange;
        const ceilWeight = weightOz + weightRange;
        const floorWeightOz = Math.floor(floorWeight);
        const ceilWeightOz = Math.ceil(ceilWeight);
        console.log(floorWeightOz, ceilWeightOz)
        try {
            const q = query(
                collection(db, 'qscLookup'),
                where('weight', '>=', Number(floorWeightOz)),
                where('weight', '<=', Number(ceilWeightOz)),
            );
            const querySnapshot = await getDocs(q);
            const shippingData = [];
            querySnapshot.forEach((doc) => {
                shippingData.push({ id: doc.id, ref: doc.ref, ...doc.data() });
            });
            const medianShippingCost = shippingData.reduce((acc, cur) => {
                return acc + Number(cur.weightedAverageCost);
            }, 0) / shippingData.length;
            console.log(medianShippingCost)
            return Number(medianShippingCost).toFixed(2);
        } catch (error) {
            console.log(error)
        }
    }

    const handleInputChange = debounceFunction((e, ref, stateSetter) => {
        e.preventDefault();
        const rawValue = ref.current.value ? parseFloat(ref.current.value) : 0;

        // Validate margin is not >= 100%
        if (rawValue >= 100) {
            toast.warning('Margin must be less than 100%. Using margin formula: Price = Cost / (1 - Margin%)', { theme: 'colored' });
            ref.current.value = '99';
            stateSetter(0.99);
            return;
        }

        const value = rawValue / 100;
        stateSetter(value);
    }, 500); // 500 ms debounce time
    
    const handleAddCostChange = debounceFunction((e, ref, stateSetter) => {
        e.preventDefault();
        const value = ref.current.value ? parseFloat(ref.current.value) : 0;
        stateSetter(value);
    }, 500); // 500 ms debounce time

    useEffect(() => {
        if (simSku) {
            const skuData = simSkuSearchResults.find((result) => result.id === simSku);
            runSimulation(skuData).then((data) => {
                setSimSkuData(data);
            });
        }
    }, [minMarginValue, targetMarginValue, b2bMarginValue, maxMarginValue, afc, acc, aic])

    return (
        <Row>
            <Col>
                <Form onSubmit={(e) => handleNewTemplateFormSubmission(e)}>
                    <Form.Group className="mb-3" controlId="templateName">
                        <Form.Label>Template Name</Form.Label>
                        <Form.Control type="text" placeholder="Enter Template Name" ref={templateNameRef}/>
                    </Form.Group>
                        <Form.Label style={{width:'15rem'}}>Applicability Period</Form.Label>
                        <Row className="mb-3"> 
                            <Col>
                                <InputGroup.Text>Start</InputGroup.Text>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date)=>setStartDate(date)}
                                    formatWeekDay={day => day.slice(0, 3)}
                                    className='form-control'
                                    placeholderText="Select Date"
                                />
                            </Col>
                            <Col>
                                <InputGroup.Text>End</InputGroup.Text>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date)=>setEndDate(date)}
                                    formatWeekDay={day => day.slice(0, 3)}
                                    className='form-control'
                                    placeholderText="Select Date"
                                />
                            </Col>
                        </Row>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>Minimum Margin</Form.Label>
                        <Form.Control type="text" placeholder="Enter the lowest acceptable profit margin" ref={minMarginRef} 
                            onChange={(e) => handleInputChange(e, minMarginRef, setMinMarginValue)}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>Target Margin</Form.Label>
                        <Form.Control type="text" placeholder="Enter the preferred margin" ref={targetMarginRef}
                            onChange={(e) => handleInputChange(e, targetMarginRef, setTargetMarginValue)}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>B2B Margin</Form.Label>
                        <Form.Control type="text" placeholder="Enter the desired B2B margin" ref={b2bMarginRef}
                            onChange={(e) => handleInputChange(e, b2bMarginRef, setB2bMarginValue)}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>Maximum Margin</Form.Label>
                        <Form.Control type="text" placeholder="Enter the maximum acceptable profit margin" ref={maxMarginRef}
                            onChange={(e) => handleInputChange(e, maxMarginRef, setMaxMarginValue)}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted d-block mb-3" style={{marginLeft:'15rem'}}>
                        Margin formula: Price = Cost / (1 - Margin%)
                    </Form.Text>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>Assigned Channel</Form.Label>
                        <Form.Select aria-label="Default select example">
                            <option>Select a designated channel</option>
                            <option value="1">Amazon</option>
                            <option value="2" disabled>Shopify</option>
                            <option value="3" disabled>Headless</option>
                        </Form.Select>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>Fulfillment Type</Form.Label>
                        <Form.Select aria-label="Default select example">
                            <option>Select a fulfillment method</option>
                            <option value="1">Self/Merchant</option>
                            <option value="2" disabled>Marketplace</option>
                            <option value="3" disabled>Third Party Provider</option>
                        </Form.Select>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>Additional Fulfillment Costs</Form.Label>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control type="text" placeholder="Enter additional fulfillment costs" ref={additionalFulfillmentCostsRef}
                            onChange={(e) => handleAddCostChange(e, additionalFulfillmentCostsRef, setAfc)}
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>Additional Channel Costs</Form.Label>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control type="text" placeholder="Enter additional channel costs" ref={additionalChannelCostsRef}
                            onChange={(e) => handleAddCostChange(e, additionalChannelCostsRef, setAcc)}
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Label style={{width:'15rem'}}>Additional Inbound Costs</Form.Label>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control type="text" placeholder="Enter additional inbound costs" ref={additionalInboundCostsRef}
                            onChange={(e) => handleAddCostChange(e, additionalInboundCostsRef, setAic)}
                        />
                    </InputGroup>
                    <div className='text-end'>
                        <Button variant="primary" type="submit">
                            Create New Pricing Template
                        </Button>
                    </div>
                </Form>
            </Col>
            <Col>
                <Row>
                    <Col>
                        Pricing Simulator (Beta) {simulationRunning ? '⏳' : null}
                    </Col>
                    <Col>
                        <Form onSubmit={(e) => lookupSkuForSimulation(e)}>
                            <Form.Group className="mb-3" controlId="skuSim" >
                            <InputGroup className="mb-3">
                                <Form.Control type="text" placeholder="Enter SKU" ref={simSkuInput}/>
                                <Button variant="primary" type="submit" onClick={(e) => lookupSkuForSimulation(e)}>
                                    🔎
                                </Button>
                                <Button variant="danger" type="submit" onClick={(e) => clearSimulation(e)}>
                                    🗑️
                                </Button>
                            </InputGroup>
                            </Form.Group>
                        </Form>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {!simSku ? (
                            <ListGroup>
                                {simSkuSearchResults.map((result) => {
                                    return (
                                        <ListGroup.Item key={result.id} onClick={() => selectSkuForSimulation(result.id)}>
                                            {result.sku}
                                        </ListGroup.Item>
                                    )
                                })}
                            </ListGroup>
                        )
                        : (
                            !errorState ? (
                                <div>
                                    <Row>
                                        <Col>
                                            <h6>
                                                {simSkuData?.title}
                                            </h6>
                                            <div className="mb-2 text-muted small">
                                                Weight: {Number(simSkuData?.weightOz).toFixed(2)} oz |
                                                Marketplace Fees: {((simSkuData?.marketplaceFees || 0) * 100).toFixed(0)}%
                                            </div>

                                            {/* Price Breakdown Card showing cost components */}
                                            {simSkuData?.priceBreakdown && (
                                                <PriceBreakdownCard breakdown={simSkuData.priceBreakdown} />
                                            )}

                                            {/* Price Range Summary */}
                                            <div className="mt-3 p-2 bg-light rounded">
                                                <strong>Price Range:</strong>{' '}
                                                {formatCurrency(simSkuData?.minPrice)} (min) to{' '}
                                                {formatCurrency(simSkuData?.maxPrice)} (max)
                                                <br />
                                                <small className="text-muted">
                                                    B2B: {formatCurrency(simSkuData?.businessPrice)}
                                                </small>
                                            </div>
                                        </Col>
                                        <Col>
                                            <Image src={productImageUrl} alt={simSkuData?.title} style={{maxWidth:'50%'}}/>
                                        </Col>
                                    </Row>
                                </div>
                            )
                            : (
                                <div>
                                    {errorMessage}
                                </div>
                            )
                            
                        )}
                    </Col>
                </Row>
            </Col>
        </Row>
    )
}


const ManageExistingTemplatesForm = ({templates}) => {
    return (
        <ListGroup>
            {templates.map((template) => {
                return (
                    <ListGroup.Item key={template.id}>
                        <Row>
                            <Col>
                                {template.name}
                            </Col>
                            {/* button should flow to the right side **/}
                            <Col className='text-end'>
                                <Button variant="danger" size="sm" onClick={(e) => deleteTemplate(e, template.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </Col>
                        </Row>
                    </ListGroup.Item>
                )
            })}
        </ListGroup>
    )
}

const deleteTemplate = async (e, templateId) => {
    e.preventDefault();
    try {
        await deleteDoc(doc(db, 'systemTemplates', templateId));
        toast.success('Template deleted', { theme: 'colored' });
    } catch (error) {
        console.log(error);
        toast.error(error.message, { theme: 'colored' });
    }
}
